import { useEffect } from "react";
import {
  usePackageActivitiesQuery,
  usePackageActivityCheckStatusQuery,
  usePackageActivityQuery,
} from "#graphql/supergraphHooks";
import { isFayAuthFailure } from "../utils/fayAuthErrors";
import { useReportFayAuthFailure } from "./useFayAuthGate";
import type {
  PackageActivitiesQuery,
  PackageActivityCheckStatusQuery,
  PackageActivityQuery,
} from "#graphql/supergraphTypes";
import type { ApolloError } from "@apollo/client";

/** One collapsed row in the Activity tab list. */
export type ActivityRow = PackageActivitiesQuery["packageActivities"][number];

/** The expanded Activity card, fetched only once a row is opened. */
export type ActivityDetail = NonNullable<PackageActivityQuery["packageActivity"]>;

/** One evaluated review note on an expanded activity. */
export type ActivityReviewNote = ActivityDetail["reviewNotes"][number];

/** One provenance record (the system the activity was read out of). */
export type ActivitySource = ActivityDetail["sources"][number];

/** Package-level roll-up of the activity checks, shown as the tab header. */
export type ActivityCheckStatus =
  PackageActivityCheckStatusQuery["packageEntityActivityCheckStatus"];

/**
 * Reports a terminal fay auth failure up to the page gate so the whole page
 * swaps to the generic error page rather than leaving the tab to render an
 * inline "Failed to load…" line. Mirrors `usePackageDetail`, minus the
 * `stopPolling` these non-polling reads don't need.
 */
function useReportAuthFailure(error: ApolloError | undefined): void {
  const reportAuthFailure = useReportFayAuthFailure();
  useEffect(() => {
    if (error && isFayAuthFailure(error)) {
      reportAuthFailure(error);
    }
  }, [error, reportAuthFailure]);
}

export type PackageActivitiesData = {
  activities: readonly ActivityRow[];
  loading: boolean;
  error?: ApolloError;
};

// None of the three reads below polls: activity rows are rewritten by an
// out-of-band sync, not by anything the open slideout triggers, so there is
// nothing to watch land.

/**
 * Lists the activities behind a package. `skip`s while the slideout is closed
 * or before the host app resolves its arena, so fay's required `arenaId` is
 * never issued as NaN.
 */
export function usePackageActivities(
  packageId: string | null,
  arenaId: number | undefined,
): PackageActivitiesData {
  const { data, loading, error } = usePackageActivitiesQuery({
    variables: { arenaId: arenaId!, packageId: packageId ?? "" },
    skip: !packageId || arenaId === undefined,
    fetchPolicy: "cache-and-network",
  });

  useReportAuthFailure(error);

  return { activities: data?.packageActivities ?? [], loading, error };
}

export type PackageActivityData = {
  activity?: ActivityDetail;
  loading: boolean;
  error?: ApolloError;
  /** The server explicitly returned no activity for this id — a row the sync
   *  has since removed, or an id from another arena. Distinguished from a
   *  still-skipped read so an opened card doesn't sit silently empty. */
  notFound: boolean;
};

/**
 * Fetches one activity's expanded card (review notes, provenance). Called only
 * from an opened row, so the tab pays for the detail read per expansion rather
 * than for every row up front.
 */
export function usePackageActivity(
  activityId: string | null,
  arenaId: number | undefined,
): PackageActivityData {
  const { data, loading, error } = usePackageActivityQuery({
    variables: { arenaId: arenaId!, id: activityId ?? "" },
    skip: !activityId || arenaId === undefined,
    fetchPolicy: "cache-and-network",
  });

  useReportAuthFailure(error);

  // Keyed off an explicitly received `packageActivity: null` rather than a
  // missing activity: while the read is skipped there is no data at all, and
  // anything derived from "no activity object" would false-positive there.
  const notFound =
    !!activityId &&
    arenaId !== undefined &&
    !loading &&
    !error &&
    data !== undefined &&
    data.packageActivity === null;

  return { activity: data?.packageActivity ?? undefined, loading, error, notFound };
}

export type PackageActivityCheckStatusData = {
  status?: ActivityCheckStatus;
  loading: boolean;
  error?: ApolloError;
};

/**
 * Reads the package's activity-check roll-up (passed / failed / skipped / not
 * evaluated) for the tab header, so a reviewer sees the overall verdict without
 * expanding every row.
 */
export function usePackageActivityCheckStatus(
  packageId: string | null,
  arenaId: number | undefined,
): PackageActivityCheckStatusData {
  const { data, loading, error } = usePackageActivityCheckStatusQuery({
    variables: { arenaId: arenaId!, packageId: packageId ?? "" },
    skip: !packageId || arenaId === undefined,
    fetchPolicy: "cache-and-network",
  });

  useReportAuthFailure(error);

  return { status: data?.packageEntityActivityCheckStatus, loading, error };
}
